class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.recommendationsCache = null;
    this.currentFirstItemId = null;
    this.cachedDesktopHTML = null;
    this.cachedMobileHTML = null;
    
    // Local storage keys
    this.STORAGE_KEY_PREFIX = 'cartRecommendations_';
    this.STORAGE_EXPIRY_KEY = 'cartRecommendationsExpiry';
    this.CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
    
    // Bind event handlers
    this.handleCardClick = this.handleCardClick.bind(this);
    this.handleQuickAddClick = this.handleQuickAddClick.bind(this);
    
    // Listen to cart updates
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.handleCartUpdate();
    });
  }

  connectedCallback() {
    this.init();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  async init() {
    try {
      const cart = await this.getCart();
      if (cart?.items?.length > 0) {
        await this.updateRecommendations(cart);
      } else {
        this.hideHeaders();
      }
    } catch (error) {
      console.error('Error initializing recommendations:', error);
      this.hideHeaders();
    }
  }

  async handleCartUpdate() {
    try {
      const cart = await this.getCart();
      await this.updateRecommendations(cart);
    } catch (error) {
      console.error('Error handling cart update:', error);
    }
  }

  async getCart() {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      if (!response.ok) throw new Error('Network response was not ok');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return { items: [] };
    }
  }

  async updateRecommendations(cart, immediate = false) {
    const desktopContainer = document.getElementById('container-recommended-products-desktop');
    const mobileContainer = document.getElementById('container-recommended-products-mobile');
    
    if (!desktopContainer && !mobileContainer) return;
  
    // Handle empty cart
    if (!cart?.items?.length) {
      this.clearContainers(desktopContainer, mobileContainer);
      this.hideHeaders();
      
      if (immediate) {
        // Signal that recommendations update is complete
        document.dispatchEvent(new CustomEvent('cart:recommendations-updated'));
      }
      return;
    }
  
    // Find the first non-sample product ID in cart
    let productToUse = null;
    
    // Find the first item that is not a Free Fabric Sample
    for (const item of cart.items) {
      // Check if this item is a Free Fabric Sample
      const isSample = item.properties && item.properties._type === "Free Fabric Sample";
      
      if (!isSample) {
        productToUse = item.product_id;
        break;
      }
    }
    
    // If all items are samples, hide recommendations
    if (!productToUse) {
      this.clearContainers(desktopContainer, mobileContainer);
      this.hideHeaders();
      
      // Signal that recommendations update is complete
      document.dispatchEvent(new CustomEvent('cart:recommendations-updated'));

      return;
    }
    
    this.showHeaders();
    
    // Check if first item has changed
    if (productToUse === this.currentFirstItemId && this.recommendationsCache) {
      // Use in-memory cache if first item hasn't changed
      this.restoreFromCache(desktopContainer, mobileContainer);
      
      if (immediate) {
        // Signal that recommendations update is complete
        document.dispatchEvent(new CustomEvent('cart:recommendations-updated'));
      }
      return;
    }
  
    // Try to get from local storage first
    const cachedData = this.getFromLocalStorage(productToUse);
    if (cachedData) {
      // Use cached data from local storage
      this.recommendationsCache = cachedData.products;
      this.currentFirstItemId = productToUse;
      this.cachedDesktopHTML = cachedData.desktopHTML;
      this.cachedMobileHTML = cachedData.mobileHTML;
      
      // Render from local storage
      this.restoreFromCache(desktopContainer, mobileContainer);
      
      if (immediate) {
        // Signal that recommendations update is complete
        document.dispatchEvent(new CustomEvent('cart:recommendations-updated'));
      }
      return;
    }
  
    // Fetch new recommendations if not in cache
    await this.fetchAndRenderRecommendations(productToUse, desktopContainer, mobileContainer, immediate);
  }

  // Update to allow signaling when fetch and render is complete
  async fetchAndRenderRecommendations(productId, desktopContainer, mobileContainer, immediate = false) {
    try {
      // Fetch recommendations for product
      const response = await fetch(
        `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=3&intent=related`
      );
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      const products = data?.products || [];

      if (!products.length) {
        this.clearContainers(desktopContainer, mobileContainer);
        this.hideHeaders();
        
        if (immediate) {
          // Signal that recommendations update is complete
          document.dispatchEvent(new CustomEvent('cart:recommendations-updated'));
        }
        return;
      }

      // Update cache
      this.recommendationsCache = products;
      this.currentFirstItemId = productId;

      // Fetch and render product HTML
      const recommendationsHTML = await Promise.all(
        products.map(product => this.fetchProductHTML(product))
      );

      // Render desktop recommendations
      if (desktopContainer) {
        const desktopHTML = recommendationsHTML
          .map((html, index) => this.extractDesktopHTML(html, products[index]))
          .filter(Boolean)
          .join('');
        await this.preloadImagesFromHTML(desktopHTML);
        this.cachedDesktopHTML = desktopHTML;
        desktopContainer.innerHTML = desktopHTML;
      }

      // Render mobile recommendations
      if (mobileContainer) {
        const mobileHTML = recommendationsHTML
          .map((html, index) => this.extractMobileHTML(html, products[index]))
          .filter(Boolean)
          .join('');
        await this.preloadImagesFromHTML(mobileHTML);
        this.cachedMobileHTML = mobileHTML;
        mobileContainer.innerHTML = mobileHTML;
      }
      
      // Save to local storage
      this.saveToLocalStorage(productId, {
        products,
        desktopHTML: this.cachedDesktopHTML,
        mobileHTML: this.cachedMobileHTML
      });
      
      this.setupEventListeners();
      
      if (immediate) {
        // Signal that recommendations update is complete
        document.dispatchEvent(new CustomEvent('cart:recommendations-updated'));
      }

    } catch (error) {
      console.error('Error handling recommendations:', error);
      this.clearContainers(desktopContainer, mobileContainer);
      this.hideHeaders();
      
      if (immediate) {
        // Signal that recommendations update is complete even on error
        document.dispatchEvent(new CustomEvent('cart:recommendations-updated'));
      }
    }
  }


  // LocalStorage methods
  saveToLocalStorage(productId, data) {
    try {
      // Save cache data
      localStorage.setItem(
        `${this.STORAGE_KEY_PREFIX}${productId}`, 
        JSON.stringify(data)
      );
      
      // Update or create expiry entry
      let expiryData = this.getExpiryData();
      const expiryTimestamp = Date.now() + this.CACHE_DURATION_MS;
      expiryData[productId] = expiryTimestamp;
      
      // Save expiry data
      localStorage.setItem(
        this.STORAGE_EXPIRY_KEY, 
        JSON.stringify(expiryData)
      );
      
      // Clean up old cache entries
      this.cleanupExpiredCache();
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      // Silently fail - local storage might be full or disabled
    }
  }

  getFromLocalStorage(productId) {
    try {
      // Get expiry data
      const expiryData = this.getExpiryData();
      const expiryTimestamp = expiryData[productId];
      
      // Check if cache is expired
      if (!expiryTimestamp || Date.now() > expiryTimestamp) {
        return null;
      }
      
      // Get and parse cache data
      const cacheData = localStorage.getItem(`${this.STORAGE_KEY_PREFIX}${productId}`);
      return cacheData ? JSON.parse(cacheData) : null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  }

  getExpiryData() {
    try {
      const expiryData = localStorage.getItem(this.STORAGE_EXPIRY_KEY);
      return expiryData ? JSON.parse(expiryData) : {};
    } catch (error) {
      return {};
    }
  }

  cleanupExpiredCache() {
    try {
      const now = Date.now();
      const expiryData = this.getExpiryData();
      let modified = false;
      
      // Check each entry for expiration
      for (const [productId, timestamp] of Object.entries(expiryData)) {
        if (now > timestamp) {
          // Remove expired entry
          localStorage.removeItem(`${this.STORAGE_KEY_PREFIX}${productId}`);
          delete expiryData[productId];
          modified = true;
        }
      }
      
      // Save updated expiry data
      if (modified) {
        localStorage.setItem(
          this.STORAGE_EXPIRY_KEY, 
          JSON.stringify(expiryData)
        );
      }
    } catch (error) {
      console.error('Error cleaning up expired cache:', error);
    }
  }

  // Simple method to preload images from HTML
  preloadImagesFromHTML(html) {
    return new Promise(resolve => {
      // Create temporary container
      const temp = document.createElement('div');
      temp.innerHTML = html;
      
      // Find all images
      const images = temp.querySelectorAll('img');
      if (images.length === 0) {
        return resolve(); // No images to load
      }
      
      let loadedCount = 0;
      const totalImages = images.length;
      
      // Set up image loading tracking
      images.forEach(img => {
        if (img.complete) {
          loadedCount++;
          if (loadedCount === totalImages) resolve();
        } else {
          img.onload = img.onerror = () => {
            loadedCount++;
            if (loadedCount === totalImages) resolve();
          };
        }
      });
    });
  }

  async fetchProductHTML(product) {
    if (!product?.handle) return '';
    
    try {
      const response = await fetch(
        `${window.Shopify.routes.root}products/${product.handle}?section_id=people-also-bought`
      );
      if (!response.ok) throw new Error('Network response was not ok');
      return await response.text();
    } catch (error) {
      console.error(`Error fetching product ${product.handle}:`, error);
      return '';
    }
  }

  // Extract HTML from fetched product templates
  extractDesktopHTML(html, product) {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const desktopElement = tempDiv.querySelector('.desktop-recommendation');
    
    // Add product tags to quick-add-to-cart if product data includes tags
    if (desktopElement && product?.tags) {
      const quickAddElement = desktopElement.querySelector('quick-add-to-cart');
      if (quickAddElement) {
        quickAddElement.setAttribute('data-product-tags', product.tags.join(','));
      }
    }
    
    return desktopElement ? desktopElement.outerHTML : '';
  }

  extractMobileHTML(html, product) {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const mobileElement = tempDiv.querySelector('.mobile-recommendation');
    
    // Add product tags to quick-add-to-cart if product data includes tags
    if (mobileElement && product?.tags) {
      const quickAddElement = mobileElement.querySelector('quick-add-to-cart');
      if (quickAddElement) {
        quickAddElement.setAttribute('data-product-tags', product.tags.join(','));
      }
    } else {
      console.log('No tags found for product:', product?.title);
    }
    
    return mobileElement ? mobileElement.outerHTML : '';
  }

  // Event handlers
  handleCardClick(event) {
    const card = event.target.closest('.desktop-recommendation, .mobile-recommendation');
    if (!card || event.target.closest('quick-add-to-cart')) return;
    
    const productId = card.getAttribute('data-id');
    if (productId) {
      window.location.href = `/products/${productId}`;
    }
  }

  handleQuickAddClick(event) {
    const quickAddToCart = event.target.closest('quick-add-to-cart');
    if (!quickAddToCart) return;

    event.preventDefault();
    event.stopPropagation();

    const variantInput = quickAddToCart.querySelector('input[name="id"]');
    if (!variantInput) return;

    // Get product tags and calculate lowest delivery cost
    const productTags = quickAddToCart.getAttribute('data-product-tags');
    
    const lowestDeliveryCost = productTags && 
      typeof delivery_tabs !== 'undefined' && 
      delivery_tabs.courierData?.length > 0
        ? delivery_tabs.calculateLowestDeliveryCost(productTags) 
        : null;
    
    // Build the item object with properties
    const item = { 
      id: variantInput.value, 
      quantity: 1 
    };
    
    if (lowestDeliveryCost !== null) {
      item.properties = {
        'Lowest Delivery Cost': String(lowestDeliveryCost)
      };
    }
    
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [item]
      })
    })
    .then(response => response.ok ? response.json() : Promise.reject('Add failed'))
    .then(data => fetch(`${window.Shopify.routes.root}cart?section_id=cart-drawer`))
    .then(response => response.ok ? response.text() : Promise.reject('Fetch failed'))
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      const newItemsContent = doc.querySelector('cart-drawer-items');
      
      if (cartDrawerItems && newItemsContent) {
        cartDrawerItems.innerHTML = newItemsContent.innerHTML;
      }
      
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.classList.add('active');
      }

      // Trigger cart update
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    })
    .catch((error) => console.error('Error:', error));
  }

  // Helper methods
  setupEventListeners() {
    // Remove existing event listeners
    this.removeEventListeners();
  
    const desktopContainer = document.getElementById('container-recommended-products-desktop');
    const mobileContainer = document.getElementById('container-recommended-products-mobile');
  
    if (desktopContainer) {
      desktopContainer.addEventListener('click', this.handleCardClick);
    }
    if (mobileContainer) {
      mobileContainer.addEventListener('click', this.handleCardClick);
      mobileContainer.addEventListener('click', this.handleQuickAddClick);
    }
  
    // Custom: Product Recommendations
    // Publish the cart-recommendation event
    publish(PUB_SUB_EVENTS.cartRecommendations, { source: 'cart-recommendations' });
    
    // Dispatch a DOM event to signal that recommendations are updated
    // This is used by the cart-items component to know when to update
    document.dispatchEvent(new CustomEvent('cart:recommendations-updated'));
  }

  removeEventListeners() {
    const desktopContainer = document.getElementById('container-recommended-products-desktop');
    const mobileContainer = document.getElementById('container-recommended-products-mobile');

    if (desktopContainer) {
      desktopContainer.removeEventListener('click', this.handleCardClick);
    }
    if (mobileContainer) {
      mobileContainer.removeEventListener('click', this.handleCardClick);
      mobileContainer.removeEventListener('click', this.handleQuickAddClick);
    }
  }

  restoreFromCache(desktopContainer, mobileContainer) {
    if (desktopContainer && this.cachedDesktopHTML) {
      desktopContainer.innerHTML = this.cachedDesktopHTML;
    }
    if (mobileContainer && this.cachedMobileHTML) {
      mobileContainer.innerHTML = this.cachedMobileHTML;
    }
    
    // Inject product tags into cached HTML
    if (this.recommendationsCache && Array.isArray(this.recommendationsCache)) {
      this.injectProductTags(desktopContainer, mobileContainer);
    }
    
    this.setupEventListeners();
  }
  
  injectProductTags(desktopContainer, mobileContainer) {
    // Inject tags into desktop recommendations
    if (desktopContainer) {
      const desktopCards = desktopContainer.querySelectorAll('.desktop-recommendation');
      desktopCards.forEach((card, index) => {
        const product = this.recommendationsCache[index];
        if (product?.tags) {
          const quickAddElement = card.querySelector('quick-add-to-cart');
          if (quickAddElement) {
            quickAddElement.setAttribute('data-product-tags', product.tags.join(','));
          }
        }
      });
    }
    
    // Inject tags into mobile recommendations
    if (mobileContainer) {
      const mobileCards = mobileContainer.querySelectorAll('.mobile-recommendation');
      mobileCards.forEach((card, index) => {
        const product = this.recommendationsCache[index];
        if (product?.tags) {
          const quickAddElement = card.querySelector('quick-add-to-cart');
          if (quickAddElement) {
            quickAddElement.setAttribute('data-product-tags', product.tags.join(','));
          }
        }
      });
    }
  }

  clearContainers(desktopContainer, mobileContainer) {
    if (desktopContainer) desktopContainer.innerHTML = '';
    if (mobileContainer) mobileContainer.innerHTML = '';
    this.recommendationsCache = null;
    this.currentFirstItemId = null;
    this.cachedDesktopHTML = null;
    this.cachedMobileHTML = null;
  }

  showHeaders() {
    this.updateHeadersVisibility(true);
  }

  hideHeaders() {
    this.updateHeadersVisibility(false);
  }

  updateHeadersVisibility(show) {
    const headerDesktop = document.querySelectorAll('.overlay-header');
    const headerMobile = document.querySelectorAll('.cart__people-bought-us-header');
    
    if (headerDesktop?.[0]) {
      headerDesktop[0].style.display = show ? 'block' : 'none';
    }
    if (headerMobile?.[0]) {
      headerMobile[0].style.display = show ? 'block' : 'none';
    }
  }
}

customElements.define('cart-recommendations', CartRecommendations);